import {
  getEnergyLabelResult,
  getProductType,
  isFridgeType,
} from "@/components/EnergySaving/utils.ts";
import { EnergyLabelsResponse } from "@/components/EnergySaving/data/EnergyLabelsFetcher.ts";
import { Variant } from "@/types/types.ts";
import {
  EnergyLabelMetaAverageUsage,
  EnergyLabelProductFoundResult,
} from "@/components/EnergySaving/data/energyLabelResponseSchema.ts";
import styles from "./InfoPanel.module.scss";
import { RecordType } from "zod";

export type Props = {
  sellingProductType: string;
  updateResponse: EnergyLabelsResponse;
  variant: Variant;
};

const InfoPanel = ({ sellingProductType, updateResponse, variant }: Props) => {
  const productType = getProductType(sellingProductType);
  if (!updateResponse.responseMetaData || !productType) {
    return null;
  }

  const result = getEnergyLabelResult(
    updateResponse,
    variant,
  ) as EnergyLabelProductFoundResult;

  const averageTariff = updateResponse.responseMetaData.general.tariff;
  const annualCalculationData =
    updateResponse.responseMetaData.general.calculationData;
  const productTypes = updateResponse.responseMetaData.general
    .productTypes as RecordType<string, EnergyLabelMetaAverageUsage>;
  const metadata = productTypes[productType];
  if (!metadata) {
    return null;
  }

  const tariffByYear = annualCalculationData.tariffByYear as RecordType<
    string,
    string
  >;
  const energyCostByYear = result.calculationData.energyCostByYear;

  const {
    energyCostYears,
    averageUsage,
    averageUsagePeriod,
    averageUsageTimesPerPeriod,
  } = metadata;

  const { calculationData, manufacturer, sku } = result;

  const { operationalPowerConsumption } = calculationData;

  const mapAverageUsagePeriod = (averageUsagePeriod: number | null) => {
    if (!averageUsagePeriod) {
      return;
    }

    return {
      52: "week",
      365: "day",
      12: "month",
    }[averageUsagePeriod];
  };

  const energyCostText = {
    "washing-machines":
      "Energy costs for washing machines are based on the energy consumption per cycle for the eco 40-60 program as published on the product fiche or EU Energy Label.",
    "washer-dryers":
      "Energy costs for washer dryers are based on the energy consumption per cycle for the wash and dry cycle as published on the product fiche or EU Energy Label.",
    "tumble-dryers":
      "Electricity costs for tumble dryers are based on a full load using the standard cotton cupboard dry program, where the load has been washed using a spin cycle of 1000 rpm leaving a moisture content of 60%. This is the standard program data as specified by the EU Energy Label.",
    dishwashers:
      "Electricity costs for dishwashers are based on the standard cleaning cycle or Economy 50 program. This is the standard program data as specified by the EU Energy Label. Keep in mind that if you use a program such as 'intensive', or a 'speed perfect' (or similar) option, the electricity used can be up to 70% higher.",
  }[productType];

  return (
    <div className={styles.info}>
      <h2>
        How we calculate costs for{" "}
        {getProductType(sellingProductType)?.replace("-", " ")}
      </h2>

      <p>
        We use data from official sources to calculate the average electricity
        cost shown. Remember that these costs will vary according to your actual
        usage.
      </p>

      <table className={styles.info__table}>
        <thead>
          <tr>
            <th>Description</th>
            <th>Value</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Power consumption</td>
            <td>
              {operationalPowerConsumption} kWh per{" "}
              {isFridgeType(productType) ? "year" : "cycle"}
            </td>
            <td>{manufacturer}</td>
          </tr>
          <tr>
            <td>Average usage</td>
            <td>
              {isFridgeType(productType)
                ? "Always on"
                : `${averageUsage} cycles per year (~${averageUsageTimesPerPeriod} per ${mapAverageUsagePeriod(averageUsagePeriod)})`}
            </td>
            <td>{isFridgeType(productType) ? "N/A" : "Energy Saving Trust"}</td>
          </tr>
          <tr>
            <td>Average lifespan</td>
            <td>{energyCostYears} years</td>
            <td>Energy Saving Trust</td>
          </tr>
          <tr>
            <td>Average tariff</td>
            <td>{averageTariff} per kWh</td>
            <td>Ofgem</td>
          </tr>
          <tr>
            <td>Average annual tariff increase</td>
            <td>
              {annualCalculationData.annualCostPerKwhPercentageChange} % (data
              from last{" "}
              {
                annualCalculationData.annualCostPerKwhPercentageChangeNumberOfYears
              }{" "}
              years)
            </td>
            <td>BEIS</td>
          </tr>
        </tbody>
      </table>
      {energyCostText && (
        <p className={styles.info__energyCostParagraph}>{energyCostText}</p>
      )}

      <h2>
        Operating cost for the {manufacturer} {sku}
      </h2>
      <table className={styles.info__table}>
        <tbody>
          <tr>
            <td>(A) Power consumption</td>
            <td>{operationalPowerConsumption} kWh per year</td>
          </tr>
          <tr>
            <td>(B) Usage</td>
            <td>
              {isFridgeType(productType)
                ? "Always on"
                : `${averageUsage} cycles per year`}
            </td>
          </tr>
          <tr>
            <td>(C) Tariff</td>
            <td>{tariffByYear["1"]} per kWh</td>
          </tr>
          <tr>
            <td>Electricity cost calculation</td>
            <td>(A * C)/100 = {energyCostByYear["1"]}</td>
          </tr>
        </tbody>
      </table>
      <h2 className={styles.info__energyCostParagraph}>
        Operating cost for the {manufacturer} {sku} over{" "}
        {Object.keys(energyCostByYear).length} years
      </h2>
      <table className={styles.info__table}>
        <tbody>
          {Object.keys(energyCostByYear).map((year) => (
            <tr key={year}>
              <td>
                Year {year} (@{tariffByYear[year]}p)
              </td>
              <td>{energyCostByYear[year]}</td>
            </tr>
          ))}
          <tr>
            <td>
              Total electricity cost over {Object.keys(energyCostByYear).length}{" "}
              years
            </td>
            <td>{result.calculationData.totalEnergyCost}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default InfoPanel;
